import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Button,
  Grid,
  Card,
  CardContent
} from '@mui/material';

interface Service {
  id: string;
  name: string;
  price: number | null;
  description: string | null;
  duration_in_minutes: number;
}

interface AvailableTime {
  iso: string;
  date: string;
  time: string;
  day_of_week: string;
  formatted: string;
}

interface Clinic {
  id: number;
  name: string;
  primary_color: string;
  logo_url?: string;
  business_id: string;
  practitioner_id: string;
}

interface BookingData {
  service: Service | null;
  appointmentTime: AvailableTime | null;
  patient: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  };
  notes: string;
}

interface ConfirmationProps {
  bookingData: BookingData;
  clinic: Clinic | null;
  onConfirm: () => void;
}

const Confirmation: React.FC<ConfirmationProps> = ({ 
  bookingData, 
  clinic,
  onConfirm 
}) => {
  const formatPrice = (price: number | null) => {
    if (price === null || price === undefined) return 'Price on consultation';
    if (typeof price !== 'number') return `$${price}`;  
    return `$${price.toFixed(2)}`;
  };
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Confirm Your Booking
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Please review your booking details below before confirming.
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Appointment Details
              </Typography>
              
              {bookingData.service && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Service
                  </Typography>
                  <Typography variant="body1">
                    {bookingData.service.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatPrice(bookingData.service.price)} • {bookingData.service.duration_in_minutes} minutes
                  </Typography>
                </Box>
              )}
              
              {bookingData.appointmentTime && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Date & Time
                  </Typography>
                  <Typography variant="body1">
                    {bookingData.appointmentTime.formatted}
                  </Typography>
                </Box>
              )}
              
              {clinic && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Clinic
                  </Typography>
                  <Typography variant="body1">
                    {clinic.name}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Patient Information
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1">
                  {bookingData.patient.first_name} {bookingData.patient.last_name}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Contact
                </Typography>
                <Typography variant="body1">
                  {bookingData.patient.email}
                </Typography>
                {bookingData.patient.phone && (
                  <Typography variant="body1">
                    {bookingData.patient.phone}
                  </Typography>
                )}
              </Box>
              
              {bookingData.notes && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Notes
                  </Typography>
                  <Typography variant="body1">
                    {bookingData.notes}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          By confirming this booking, you agree to our cancellation policy.
          Please provide at least 24 hours notice for any cancellations.
        </Typography>
        
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={onConfirm}
        >
          Confirm Booking
        </Button>
      </Box>
    </Box>
  );
};

export default Confirmation;